import { CuratedSearchDesignTabStatusEnum } from "./CuratedSearchDesignTabsContainer";
import { useMemo } from "react";
import CiroButton, { CiroButtonStyleEnum } from "../../shared/CiroButton";
import classNames from "classnames";
import {
  ClockIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  BeakerIcon,
} from "@heroicons/react/24/outline";
import {
  AutopilotSearchStrategyJobEnum,
  OpensearchCuratedSearch,
  OrgContactInternalSearchPreview,
  SearchAnalysisPreviewResults,
} from "../../../__generated__/graphql";
import CiroPill from "../../shared/CiroPill";

export type ICuratedSearchDesignTabStatus =
  | {
      state: CuratedSearchDesignTabStatusEnum.LOADING_FROM_PRIOR_SEARCH;
      savedFilters: OpensearchCuratedSearch | null | undefined;
      savedPreviews: OrgContactInternalSearchPreview | null | undefined;
      savedAnalysisPreview: SearchAnalysisPreviewResults | null | undefined;
    }
  | {
      state: CuratedSearchDesignTabStatusEnum.LOADING_FILTERS;
      savedFilters: OpensearchCuratedSearch | null | undefined;
    }
  | {
      state: CuratedSearchDesignTabStatusEnum.FINDING_CONTACTS;
      savedFilters: OpensearchCuratedSearch;
    }
  | {
      state: CuratedSearchDesignTabStatusEnum.ANALYZING_CONTACTS;
      savedFilters: OpensearchCuratedSearch;
      savedPreviews: OrgContactInternalSearchPreview;
    }
  | {
      state: CuratedSearchDesignTabStatusEnum.COMPLETED;
      savedFilters: OpensearchCuratedSearch;
      savedPreviews: OrgContactInternalSearchPreview;
      savedAnalysisPreview: SearchAnalysisPreviewResults;
      totalContact: number;
      relevantContact: number;
    };

const CuratedSearchDesignTab = ({
  activeIdx,
  idx,
  strategy,
  setActiveIdx,
  tabStatus,
  isBestStrategy,
}: {
  activeIdx: number;
  idx: number;
  strategy: AutopilotSearchStrategyJobEnum;
  setActiveIdx: (idx: number) => void;
  tabStatus: ICuratedSearchDesignTabStatus;
  isBestStrategy?: boolean;
}) => {
  const title = useMemo(() => {
    if (strategy === AutopilotSearchStrategyJobEnum.SpecificCompaniesStrategy) {
      return "Specific Companies";
    }

    if (strategy === AutopilotSearchStrategyJobEnum.IndustryKeywordsStrategy) {
      return "Industry & Keywords";
    }

    return "Custom Strategy";
  }, [strategy]);

  const loadingIconClasses = classNames(
    "w-5",
    "h-5",
    "mr-2",
    "animate-pulse",
    "duration-500",
  );

  return (
    <CiroButton
      style={CiroButtonStyleEnum.UNSTYLED}
      className={classNames("font-medium", "px-4", "py-2", "rounded", {
        "bg-orange-100": activeIdx === idx,
        "text-orange-700": activeIdx === idx,
        "border-2": activeIdx === idx,
        "border-orange-200": activeIdx === idx,
        "text-gray-600": activeIdx !== idx,
        "hover:bg-gray-100": activeIdx !== idx,
      })}
      analyticsField={`Select ${title} tab in autobuilder`}
      onClick={() => setActiveIdx(idx)}
    >
      <div className={classNames("flex", "items-center")}>
        {tabStatus.state ===
          CuratedSearchDesignTabStatusEnum.LOADING_FROM_PRIOR_SEARCH && (
          <ClockIcon className={loadingIconClasses} />
        )}
        {tabStatus.state ===
          CuratedSearchDesignTabStatusEnum.LOADING_FILTERS && (
          <FunnelIcon className={loadingIconClasses} />
        )}
        {tabStatus.state ===
          CuratedSearchDesignTabStatusEnum.FINDING_CONTACTS && (
          <MagnifyingGlassIcon className={loadingIconClasses} />
        )}
        {tabStatus.state ===
          CuratedSearchDesignTabStatusEnum.ANALYZING_CONTACTS && (
          <BeakerIcon className={loadingIconClasses} />
        )}
        {tabStatus.state === CuratedSearchDesignTabStatusEnum.COMPLETED && (
          <CheckCircleIcon
            className={classNames("w-5", "h-5", "mr-2", "text-orange-800")}
          />
        )}
        {title}
        {isBestStrategy && (
          <div className={classNames("ml-2")}>
            <CiroPill
              background="bg-orange-200"
              fontColor="text-orange-700"
              customFontWeight="font-medium"
            >
              Recommended
            </CiroPill>
          </div>
        )}
      </div>
    </CiroButton>
  );
};

export default CuratedSearchDesignTab;
